import { Box, Divider, IconButton, Stack, Typography, useTheme } from '@mui/material'
import Grid from '@mui/material/Grid2'
import { useSelector } from 'react-redux'
import { UtilityDetailSelector } from '../../../../store/utilityDetails'
import { getWaterSourceFullText } from '../../../../utils/waterSystemHelper'
import { CustomButton } from '../../../Button'
import { CloseIcon } from '../../../icons/CloseIcon'
import { PinLocationIcon } from '../../../icons/PinLocationIcon'
import WaterSystemStatsCard from './components/WaterSystemStatsCard'
import { ContaminantDetails } from './ContaminantDetails'
import { useNavigate } from 'react-router'
import { useStyles } from '../../../../theme/styles'

interface PublicUtilityDetailProps {
  onClose: (e: React.MouseEvent<Element> | React.KeyboardEvent<Element>) => void
}

export const PublicUtilityDetail: React.FC<PublicUtilityDetailProps> = ({ onClose }) => {
  const {
    PWS_NAME = '',
    CITY = '',
    STATE = '',
    ADDRESS_LINE1 = '',
    ADDRESS_LINE2 = '',
    COORDINATES,
    POPULATION_SERVED_COUNT = 0,
    PRIMARY_SOURCE_CODE = '',
    CONTAMINANT_VIOLS_COUNT = 0,
    SERVICE_CONNECTIONS_COUNT = 0,
    CONTAMINANT_VIOLS = [],
  } = useSelector(UtilityDetailSelector.makeUtility)

  const LOCATION = `${CITY}, ${STATE}`
  const navigate = useNavigate()
  const styles = useStyles()

  return (
    <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Fixed Header */}
      <Stack gap={2} p={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography 
            sx={{
              fontSize: {
                xs: '0.75rem',
                sm: '0.875rem',
                md: '1rem',   
                lg: '1.125rem'    
              }
            }}
            color="primary.dark" 
            fontWeight="bold"
          >
            {PWS_NAME}
          </Typography>
          <IconButton onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Stack>
        <Divider />
      </Stack>

      {/* Scrollable Content */}
      <Box sx={{ flex: 1, mr: 1, overflow: 'auto', ...styles.scrollbar }}>
        <Stack gap={2} px={3} sx={{ mb: 2 }}>
          <Grid container spacing={1}>
            <Grid size={12}>
              <Box display="flex" alignItems="flex-start" gap={0.5}>
                <PinLocationIcon sx={{ fontSize: { xs: 16, sm: 18, md: 20 }, mt: 0.5 }} />
                <Stack>
                  {[
                    COORDINATES?.formatted_address || ADDRESS_LINE1 || ADDRESS_LINE2
                  ]
                    .filter(Boolean)
                    .map((line, index) => (
                      <Typography 
                        key={index}
                        color="primary.dark" 
                        sx={{ fontSize: { xs: 12, sm: 13, md: 14 } }}
                      >
                        {line}
                      </Typography>
                    ))}
                </Stack>
              </Box>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Stack sx={{ mb: 2 }}>
              <Typography 
                fontSize={{ xs: '0.875rem', sm: '0.925rem', md: '1rem' }} 
                color="primary.dark" 
                fontWeight={600}
              >
                Source
              </Typography>
              <Typography 
                color="text.secondary" 
                sx={{ fontSize: { xs: 12, sm: 13, md: 14 } }}
                fontWeight={500}
              >
                {getWaterSourceFullText(PRIMARY_SOURCE_CODE)}
              </Typography>
            </Stack>
            <WaterSystemStatsCard
              totalConnections={SERVICE_CONNECTIONS_COUNT}
              totalPopulation={POPULATION_SERVED_COUNT}
              totalViolation={CONTAMINANT_VIOLS_COUNT}
            />
            <ContaminantDetails contaminantVols={CONTAMINANT_VIOLS} />
          </Grid>
        </Stack>
      </Box>
      
      <Box sx={{ pt: 2, p: 3, mr: 4.5, ml: 3, mt: 'auto', bgcolor: '#F3F6F8' }}>
        <CustomButton 
          size='medium' 
          fullWidth
          onClick={() => { navigate('/test-strip')}} 
          text="Request a water quality test strip" 
        />
        <Typography
          align="center"
          color="text.primary"
          sx={{ 
            fontSize: { xs: 10, sm: 12, md: 12 },
            mt: 1
          }}
        >
          This data applies to a broad area; let's analyze your home.
        </Typography>
      </Box>
    </Stack>
  )
}
